'use client';
import styles from './headerwcp.module.sass';
import cn from 'classnames';
import { MainMenu, MainMenuProps } from '../MainMenu';
import { SiteLogo, ISiteLogoProps } from '@intigral/shared/components/SiteLogo';
import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';
import { FontIcon } from '@intigral/shared';
import React, { useEffect, useState, forwardRef } from 'react';

export interface HeaderWCPProps extends React.HTMLAttributes<HTMLDivElement> {
    logoProps?: ISiteLogoProps;
    menuProps?: MainMenuProps | React.ReactElement;
    children?: React.ReactElement | React.ReactElement[];
    staticChildren?: React.ReactElement | React.ReactElement[];
    mobileChildren?: React.ReactElement | React.ReactElement[];
}

export const HeaderWcp = forwardRef<HTMLDivElement, HeaderWCPProps>(
    ({ menuProps, logoProps, children, staticChildren, mobileChildren, ...props }, ref) => {
        const [isNavVisible, setIsNavVisible] = useState(false);

        // const toggleNavVisibility = () => setIsNavVisible((prev) => !prev);

        const isMobile = useMediaQuery('(max-width: 924px)');
        const [isSticky, setIsSticky] = useState(false);

        const toggleNavVisibility = () => {
            setIsNavVisible((prev) => {
                const newVisibility = !prev;

                // Add or remove class based on new visibility
                if (newVisibility) {
                    document.body.classList.add('nav-visible');
                } else {
                    document.body.classList.remove('nav-visible');
                }
                return newVisibility;
            });
        };

        const handleMenuItemClick = (event: React.MouseEvent) => {
            // Close the menu only if it's not a dropdown toggle
            const target = event.target as HTMLElement;

            // Check if the click is on a dropdown toggle or item
            if (target.closest(`.${styles.expandIcon}`)) {
                return; // Do nothing if a dropdown toggle is clicked
            }

            // Close the menu if a dropdown item or non-dropdown item is clicked
            setIsNavVisible(false);
            document.body.classList.remove('nav-visible');
        };

        useEffect(() => {
            const thresholdPx = isMobile ? 100 : 300; // scroll distance in pixels before header becomes sticky
            const buffer = 20; // px buffer to prevent jitter

            const handleScroll = () => {
                const currentScrollY = window.scrollY;

                // If nav is open or we're at the very top, header should not be sticky
                if (currentScrollY === 0) {
                    setIsSticky(false);
                    return;
                }

                // Toggle sticky only when crossing thresholds to avoid thrashing
                setIsSticky((prev) => {
                    if (!prev && currentScrollY > thresholdPx + buffer) return true;
                    if (prev && currentScrollY <= thresholdPx - buffer) return false;
                    return prev;
                });
            };

            // Set initial state on mount and whenever dependencies change
            handleScroll();

            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }, [isNavVisible, isMobile]);

        return (
            <div
                ref={ref}
                className={cn(
                    styles.header,
                    props.className,
                    isSticky && styles.sticky,
                    isNavVisible && styles.navVisible,
                    !menuProps && styles.gridtwo
                )}
                {...props}
            >
                {logoProps && <SiteLogo {...logoProps} className={styles.logo} />}
                {!isMobile && (
                    <>
                        {/* {menuProps ? <MainMenu {...menuProps} /> : <div></div>} */}
                        {menuProps && React.isValidElement(menuProps) ? (
                            menuProps
                        ) : menuProps ? (
                            <MainMenu {...(menuProps as MainMenuProps)} />
                        ) : null}
                        <div className={styles.right}>
                            {children && children}
                            {staticChildren && staticChildren}
                        </div>
                    </>
                )}

                {isMobile && (
                    <>
                        <div className={cn(styles.right, isNavVisible && styles.navshown)}>
                            {staticChildren && staticChildren}
                            {menuProps && (
                                <div onClick={toggleNavVisibility} className={styles.bars}>
                                    <FontIcon
                                        {...(isNavVisible ? { name: 'close' } : { name: 'menu' })}
                                        size="smaller"
                                        md="smaller"
                                    />
                                </div>
                            )}
                        </div>
                        <div className={cn(styles.frame, isNavVisible && styles.visible)}>
                            {children && children}
                            {menuProps && !React.isValidElement(menuProps) && (
                                <MainMenu
                                    {...menuProps}
                                    onClick={menuProps.onClick ? menuProps.onClick : handleMenuItemClick}
                                    onDropdownItemClick={() => {
                                        setIsNavVisible(false); // Close the menu
                                        // document.body.classList.remove('nav-visible'); // Remove any menu visibility classes
                                    }}
                                />
                            )}
                            {/* {mobileChildren && <div className={styles.bottom}>{mobileChildren}</div>} */}
                            {mobileChildren && (
                                <div className={styles.bottom}>
                                    {React.Children.map(mobileChildren, (child) =>
                                        React.isValidElement(child)
                                            ? React.cloneElement(child as React.ReactElement<any>, {
                                                  onClick: () => {
                                                      setIsNavVisible(false);
                                                      document.body.classList.remove('nav-visible');
                                                      if ((child as React.ReactElement<any>).props.onClick) {
                                                          (child as React.ReactElement<any>).props.onClick();
                                                      }
                                                  },
                                              })
                                            : child
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
                {/* </div> */}
            </div>
        );
    }
);
